import { createApi } from '@reduxjs/toolkit/query/react';
import { Client, ClientDetail, CreateClientRequest } from '../../types/client';
import { PagedResult } from '../../types/api';
import { config } from '../../constants/config';
import { createBaseQuery } from './baseQuery';

export const clientsApi = createApi({
  reducerPath: 'clientsApi',
  baseQuery: createBaseQuery(`${config.apiUrl}/api`),
  tagTypes: ['Client'],
  endpoints: (builder) => ({
    getClients: builder.query<PagedResult<Client>, {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: string;
      organizationId?: string;
      providerId?: string;
      serialNumber?: string;
      includeDeleted?: boolean;
      sortBy?: string;
      sortDesc?: boolean;
    }>({
      query: (params) => ({
        url: '/clients',
        params: {
          page: params.page || 1,
          pageSize: params.pageSize || 20,
          ...(params.search && { search: params.search }),
          ...(params.status && { status: params.status }),
          ...(params.organizationId && { organizationId: params.organizationId }),
          ...(params.providerId && { providerId: params.providerId }),
          ...(params.serialNumber && { serialNumber: params.serialNumber }),
          ...(params.includeDeleted !== undefined && { includeDeleted: params.includeDeleted }),
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortDesc !== undefined && { sortDesc: params.sortDesc }),
        },
      }),
      providesTags: ['Client'],
    }),
    
    getAllClients: builder.query<PagedResult<Client>, {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: string;
      organizationId?: string;
      providerId?: string;
      serialNumber?: string;
      includeDeleted?: boolean;
      sortBy?: string;
      sortDesc?: boolean;
    }>({
      query: (params) => ({
        url: '/admin/clients',
        params: {
          page: params.page || 1,
          pageSize: params.pageSize || 20,
          ...(params.search && { search: params.search }),
          ...(params.status && { status: params.status }),
          ...(params.organizationId && { organizationId: params.organizationId }),
          ...(params.providerId && { providerId: params.providerId }),
          ...(params.serialNumber && { serialNumber: params.serialNumber }),
          ...(params.includeDeleted !== undefined && { includeDeleted: params.includeDeleted }),
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortDesc !== undefined && { sortDesc: params.sortDesc }),
        },
      }),
      providesTags: ['Client'],
    }),
    
    getClient: builder.query<ClientDetail, string>({
      query: (id) => `/clients/${id}`,
      providesTags: (result, error, id) => [{ type: 'Client', id }],
    }),
    
    createClient: builder.mutation<ClientDetail & { secret?: string }, CreateClientRequest>({
      query: (client) => ({
        url: '/clients/create',
        method: 'POST',
        body: client,
      }),
      invalidatesTags: ['Client'],
    }),
    
    cycleClientSecret: builder.mutation<{ secret?: string }, string>({
      query: (clientId) => ({
        url: '/clients/cycleSecret',
        method: 'POST',
        body: { id: clientId },
      }),
      invalidatesTags: (result, error, clientId) => [{ type: 'Client', id: clientId }],
    }),
    
    lockClient: builder.mutation<void, string>({
      query: (clientId) => ({
        url: '/clientManagement/lockClient',
        method: 'POST',
        body: { clientId },
      }),
      invalidatesTags: ['Client'],
    }),
    
    activateClient: builder.mutation<void, string>({
      query: (clientId) => ({
        url: '/clientManagement/activateClient',
        method: 'POST',
        body: { clientId },
      }),
      invalidatesTags: ['Client'],
    }),

    deleteClient: builder.mutation<void, string>({
      query: (clientId) => ({
        url: '/clientManagement/deleteClient',
        method: 'POST',
        body: { clientId },
      }),
      invalidatesTags: ['Client'],
    }),
    
    setClientEndpoint: builder.mutation<void, { clientId: string; endpointName: string }>({
      query: ({ clientId, endpointName }) => ({
        url: '/clients/setEndpoint',
        method: 'POST',
        body: { id: clientId, endpointName },
      }),
      invalidatesTags: (result, error, { clientId }) => [{ type: 'Client', id: clientId }],
    }),
    
    setClientUsername: builder.mutation<void, { clientId: string; username: string }>({
      query: ({ clientId, username }) => ({
        url: '/clients/setUsername',
        method: 'POST',
        body: { id: clientId, username },
      }),
      invalidatesTags: (result, error, { clientId }) => [{ type: 'Client', id: clientId }],
    }),
    
    setClientCustomerId: builder.mutation<void, { clientId: string; customerId: string }>({
      query: ({ clientId, customerId }) => ({
        url: '/clients/setCustomerId',
        method: 'POST',
        body: { id: clientId, customerId },
      }),
      invalidatesTags: (result, error, { clientId }) => [{ type: 'Client', id: clientId }],
    }),

    setClientPassword: builder.mutation<void, { clientId: string; password: string }>({
      query: ({ clientId, password }) => ({
        url: '/clients/setPassword',
        method: 'POST',
        body: { id: clientId, pw: password },
      }),
      invalidatesTags: (result, error, { clientId }) => [{ type: 'Client', id: clientId }],
    }),
    
    setCertificate: builder.mutation<void, { clientId: string; certificate: File; password?: string }>({
      query: ({ clientId, certificate, password = '' }) => {
        const formData = new FormData();
        formData.append('id', clientId);
        formData.append('certificate', certificate);
        formData.append('pw', password);
        
        return {
          url: '/clients/setCertificate',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { clientId }) => [{ type: 'Client', id: clientId }],
    }),
    
    testClientConnection: builder.mutation<{ success: boolean; error?: { errorCode: number; errorMsg: string } }, string>({
      query: (clientId) => ({
        url: '/clients/test',
        method: 'POST',
        body: { clientId },
      }),
    }),
    
    setClientApiKey: builder.mutation<void, { clientId: string; apiKey: string }>({
      query: ({ clientId, apiKey }) => ({
        url: '/clients/setApiKey',
        method: 'POST',
        body: { id: clientId, apiKey },
      }),
      invalidatesTags: (result, error, { clientId }) => [{ type: 'Client', id: clientId }],
    }),
    
    setClientKeyPair: builder.mutation<void, string>({
      query: (clientId) => ({
        url: '/clients/setKeyPair',
        method: 'POST',
        body: { id: clientId },
      }),
      invalidatesTags: (result, error, clientId) => [{ type: 'Client', id: clientId }],
    }),

    setClientSettings: builder.mutation<void, { clientId: string; clientIPs: string[] }>({
      query: ({ clientId, clientIPs }) => ({
        url: '/clients/setSettings',
        method: 'POST',
        body: { clientId, clientIPs },
      }),
      invalidatesTags: (result, error, { clientId }) => [{ type: 'Client', id: clientId }],
    }),
  }),
});

export const {
  useGetClientsQuery,
  useGetAllClientsQuery,
  useGetClientQuery,
  useCreateClientMutation,
  useCycleClientSecretMutation,
  useLockClientMutation,
  useActivateClientMutation,
  useDeleteClientMutation,
  useSetClientEndpointMutation,
  useSetClientUsernameMutation,
  useSetClientCustomerIdMutation,
  useSetClientPasswordMutation,
  useSetCertificateMutation,
  useTestClientConnectionMutation,
  useSetClientApiKeyMutation,
  useSetClientKeyPairMutation,
  useSetClientSettingsMutation,
} = clientsApi;